import React from "react";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import TimelineIcon from "@mui/icons-material/Timeline";
import GestureIcon from "@mui/icons-material/Gesture";
import StraightenIcon from "@mui/icons-material/Straighten";
import PanToolIcon from "@mui/icons-material/PanTool";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CustomButton from "../../../../../components/buttons/CustomButton";
import styles from "./CranialEditor.module.css";

const MODES = [
  { value: "landmarks", label: "Marcos", icon: <TimelineIcon fontSize="small" /> },
  { value: "contour", label: "Contorno", icon: <GestureIcon fontSize="small" /> },
  { value: "scale", label: "Escala", icon: <StraightenIcon fontSize="small" /> },
  { value: "pan", label: "Mover", icon: <PanToolIcon fontSize="small" /> },
];

export const MODE_HINTS = {
  landmarks:
    "Arraste N (nariz), as duas O (borda anterior de cada orelha) e, se quiser, o P. A é o cruzamento da reta N-P com o contorno.",
  contour: "Arraste os pontos. Toque na linha para adicionar um ponto. Toque em um ponto e use Remover.",
  scale: "Toque nas duas extremidades do marcador de referência. Arraste os pontos para ajustar.",
  pan: "Arraste para mover a imagem. Use a roda do mouse ou dois dedos para aproximar.",
};

const iconButtonSx = { width: 40, height: 40 };
const modeButtonSx = { minHeight: 40, minWidth: 40, gap: "6px", px: 1.5, textTransform: "none", fontWeight: 600 };
export const actionButtonSx = { minHeight: 40, borderRadius: "12px", textTransform: "none" };

function ToolIcon({ title, onClick, disabled, children }) {
  return (
    <Tooltip title={title}>
      {/* span keeps the tooltip working while the button is disabled */}
      <span>
        <IconButton aria-label={title} onClick={onClick} disabled={disabled} sx={iconButtonSx}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function EditorToolbar({
  mode,
  onModeChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  zoom,
  minZoom = 1,
  maxZoom = 6,
  onZoomIn,
  onZoomOut,
  onResetView,
  canRestore,
  onRestore,
  canRemovePoint,
  onRemovePoint,
  canResetPosterior,
  onResetPosterior,
}) {
  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarRow}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          color="primary"
          aria-label="Modo de edição"
          onChange={(_, value) => value && onModeChange(value)}
          className={styles.modeGroup}
        >
          {MODES.map((m) => (
            <ToggleButton key={m.value} value={m.value} aria-label={m.label} sx={modeButtonSx}>
              {m.icon}
              <span className={styles.modeLabel}>{m.label}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <div className={styles.toolGroup}>
          <ToolIcon title="Desfazer" onClick={onUndo} disabled={!canUndo}>
            <UndoIcon fontSize="small" />
          </ToolIcon>
          <ToolIcon title="Refazer" onClick={onRedo} disabled={!canRedo}>
            <RedoIcon fontSize="small" />
          </ToolIcon>
          <ToolIcon title="Afastar" onClick={onZoomOut} disabled={zoom <= minZoom}>
            <ZoomOutIcon fontSize="small" />
          </ToolIcon>
          <ToolIcon title="Aproximar" onClick={onZoomIn} disabled={zoom >= maxZoom}>
            <ZoomInIcon fontSize="small" />
          </ToolIcon>
          <ToolIcon title="Enquadrar imagem" onClick={onResetView} disabled={zoom <= minZoom}>
            <CenterFocusStrongIcon fontSize="small" />
          </ToolIcon>
        </div>
      </div>

      <div className={styles.toolbarRow}>
        <Typography variant="body2" className={styles.hint}>
          {MODE_HINTS[mode]}
        </Typography>
        <div className={styles.toolGroup}>
          {mode === "contour" ? (
            <CustomButton
              variant="outlined"
              style="error"
              size="small"
              label="Remover ponto"
              icon={<DeleteOutlineIcon fontSize="small" />}
              enabled={Boolean(canRemovePoint)}
              onClick={onRemovePoint}
              sx={actionButtonSx}
            />
          ) : null}
          {mode === "landmarks" ? (
            <CustomButton
              variant="outlined"
              style="primary"
              size="small"
              label="P automático"
              icon={<AutoFixHighIcon fontSize="small" />}
              enabled={Boolean(canResetPosterior)}
              onClick={onResetPosterior}
              sx={actionButtonSx}
            />
          ) : null}
          <CustomButton
            variant="outlined"
            style="primary"
            size="small"
            label="Restaurar sugestão da IA"
            icon={<RestartAltIcon fontSize="small" />}
            enabled={Boolean(canRestore)}
            onClick={onRestore}
            sx={actionButtonSx}
          />
        </div>
      </div>
    </div>
  );
}

export default EditorToolbar;
